import { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'
import { parseIds, toQuery } from './composables/useRouteQuery'

const STORAGE_KEY = 'mm-client'

/**
 * How a list page holds the Client filter in `client`, from its route `meta.materialsClient`:
 * several clients, or one on Imports. Other pages hold none.
 */
const pageOf = (route: RouteLocationNormalized): 'many' | 'one' | undefined => {
    const kind = route.meta.materialsClient
    return kind === 'many' || kind === 'one' ? kind : undefined
}

const clientParam = (route: RouteLocationNormalized): string | null => {
    const value = route.query.client
    return typeof value === 'string' && value !== '' ? value : null
}

/** The clients chosen last in this browser tab; none when nothing was chosen or storage is blocked. */
export const rememberedClients = (): number[] => {
    try {
        return parseIds(sessionStorage.getItem(STORAGE_KEY))
    } catch {
        return []
    }
}

const remember = (ids: number[]) => {
    try {
        if (ids.length) sessionStorage.setItem(STORAGE_KEY, ids.join(','))
        else sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        return
    }
}

/**
 * Before a list page opens without a Client filter, adds the clients chosen last, so moving between
 * the pages keeps the client. Imports takes one client: it is carried only when one was chosen. A
 * link that names its clients opens as it is, and a change within the page (Clear all) stays.
 */
export const carryClient = (to: RouteLocationNormalized, from: RouteLocationNormalized): RouteLocationRaw | true => {
    const page = pageOf(to)
    if (!page || to.name === from.name || clientParam(to) !== null) return true
    const ids = rememberedClients()
    if (!ids.length || (page === 'one' && ids.length > 1)) return true
    return { path: to.path, hash: to.hash, query: toQuery(to.query, { client: ids }), replace: true }
}

/**
 * After a list page settles, keeps its Client filter as the one to carry. Imports without a client
 * keeps several clients chosen elsewhere, which it cannot take, and clears a single one.
 */
export const recordClient = (to: RouteLocationNormalized) => {
    const page = pageOf(to)
    if (!page) return
    const ids = parseIds(clientParam(to))
    if (page === 'one' && !ids.length && rememberedClients().length > 1) return
    remember(page === 'one' ? ids.slice(0, 1) : ids)
}
